import { Link } from 'react-router-dom';
import { LandingShell } from './LandingShell';

// LandingHub — public-sidan på vuelette.com/ när användaren INTE är
// inloggad. När de är inloggade redirectar App.tsx till produkten.

const REQUEST_ACCESS_MAILTO =
  'mailto:henrik@hallengrens.com?subject=Vuelette%20%E2%80%93%20Request%20access';

export default function LandingHub() {
  return (
    <LandingShell currentPage="hub">
      {/* Hero — full-bleed image right, fading to dark on the left.
          Bilden själv har inbyggd vänster-gradient mot svart; vi förstärker
          den med en CSS-overlay för att blandas helt med bg. */}
      <header className="vl-hero">
        <div
          className="vl-hero-image-bg"
          style={{ backgroundImage: 'url(/landing-img/hero-hub-v8.jpg)' }}
          aria-hidden
        />
        <div className="vl-hero-content">
          <div className="vl-hero-text">
            <div className="vl-hero-eyebrow vl-mono">
              Vuelette · Curation studio for image-makers
            </div>
            <h1>
              Send something <em>worth opening.</em>
            </h1>
            <p className="vl-lead">
              A quiet, considered home for your photographs. Vuelette is
              where shoots become collections, collections become portfolios,
              and portfolios become living links you can share — the way
              you'd send a book, not a folder.
            </p>
            <div className="vl-actions">
              <a href={REQUEST_ACCESS_MAILTO} className="vl-btn vl-btn-primary">
                Request access →
              </a>
              <a href="#how" className="vl-btn">How it works</a>
            </div>
          </div>
        </div>
      </header>

      {/* Three audiences */}
      <section className="vl-section" id="audiences">
        <div className="vl-container">
          <div className="vl-section-eyebrow">For whom</div>
          <h2 className="vl-section-title">
            Built for the people who make <em>and share</em> images.
          </h2>
          <p className="vl-section-lead">
            Whether you're submitting to an agency, delivering to a client,
            or working alongside someone you trust — Vuelette gives the
            same care to the work and the way you present it.
          </p>

          <div className="vl-audience-grid">
            <Link to="/for-models" className="vl-audience-card">
              <div className="vl-card-img">
                <img src="/landing-img/model-card-v4.jpg" alt="" />
              </div>
              <div className="vl-card-body">
                <div className="vl-audience-label">For models</div>
                <h3>Your work, <em>permanently yours.</em></h3>
                <p>
                  Every shoot lives with you, not just with whoever pressed
                  the shutter. Curate, organize, and send agency-ready
                  portfolios from one quiet home.
                </p>
                <span className="vl-arrow">Explore <span>→</span></span>
              </div>
            </Link>

            <Link to="/for-photographers" className="vl-audience-card">
              <div className="vl-card-img">
                <img src="/landing-img/hero-photographer-v6.jpg" alt="" />
              </div>
              <div className="vl-card-body">
                <div className="vl-audience-label">For photographers</div>
                <h3>From shoot to <em>delivery</em>, in one place.</h3>
                <p>
                  Import a session, work through ratings and selects, and
                  deliver living portfolios that update when your work
                  does. No more WeTransfer, no more dead links.
                </p>
                <span className="vl-arrow">Explore <span>→</span></span>
              </div>
            </Link>

            <Link to="/for-duos" className="vl-audience-card">
              <div className="vl-card-img">
                <img src="/landing-img/hero-duos-v6.jpg" alt="" />
              </div>
              <div className="vl-card-body">
                <div className="vl-audience-label">For creative duos</div>
                <h3>For partners who <em>work together.</em></h3>
                <p>
                  Built first for a creator-photographer couple in Barcelona.
                  Rate, comment, and curate the same shoot side by side —
                  both of you keep your own view of the work.
                </p>
                <span className="vl-arrow">Explore <span>→</span></span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="vl-section" id="how">
        <div className="vl-container">
          <div className="vl-section-eyebrow">How it works</div>
          <h2 className="vl-section-title">
            Three layers, each doing <em>one thing well.</em>
          </h2>
          <p className="vl-section-lead">
            Vuelette is structured around how you actually think about
            photographs — from raw material to finished impression.
          </p>

          <div className="vl-how-grid">
            <div className="vl-how-step">
              <div className="vl-how-num">01 · Shoots</div>
              <h4>Where the raw work lives.</h4>
              <p>
                Import a session. Rate frames, favorite the strongest,
                leave private notes. Both you and your collaborator keep
                your own ratings and favorites — never overwritten.
              </p>
            </div>
            <div className="vl-how-step">
              <div className="vl-how-num">02 · Sets</div>
              <h4>Where you curate across sessions.</h4>
              <p>
                Pull the best frames from anywhere into a Set.
                "Editorial selects." "Beauty work." "Things I'd shoot
                again." A frame can live in many sets without being
                duplicated.
              </p>
            </div>
            <div className="vl-how-step">
              <div className="vl-how-num">03 · Portfolios</div>
              <h4>Where the work goes out.</h4>
              <p>
                Build a public link from one or two sets, with the meta
                you choose — measurements, contact, intro. The link stays
                live: when you update the set, the portfolio updates too.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Living links */}
      <section className="vl-section">
        <div className="vl-container">
          <div className="vl-split vl-split-right">
            <div className="vl-split-text">
              <div className="vl-section-eyebrow">What makes it different</div>
              <h2 className="vl-section-title">
                Links that <em>stay alive</em> long after you press send.
              </h2>
              <p className="vl-section-lead">
                Most portfolios are snapshots — frozen the moment you
                exported them. Vuelette portfolios read from your curation
                directly, so every time you update a Set, every link
                you've ever sent reflects the new version.
              </p>
              <ul className="vl-features">
                <li>
                  <span className="vl-feat-icon">∞</span>
                  <div>
                    <strong>One source, many links</strong>
                    <span>Send the same portfolio to multiple recipients. Update once — they all see the change.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⌥</span>
                  <div>
                    <strong>Three access modes</strong>
                    <span>Public, password-protected, or expiring. Pick per portfolio.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⌖</span>
                  <div>
                    <strong>Built-in tracking</strong>
                    <span>See who opened, how long they stayed, and which frames they lingered on.</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="vl-split-image">
              <img src="/landing-img/prints-macro-v5.jpg" alt="" />
            </div>
          </div>
        </div>
      </section>

      {/* Collab section */}
      <section className="vl-section">
        <div className="vl-container">
          <div className="vl-split vl-split-left">
            <div className="vl-split-image">
              <img
                src="/landing-img/duos-parallel-v2.jpg"
                alt="Two people working in parallel on the same shoot"
              />
            </div>
            <div className="vl-split-text">
              <div className="vl-section-eyebrow">Shared, not handed-over</div>
              <h2 className="vl-section-title">
                Two people, <em>one shoot,</em> two perspectives.
              </h2>
              <p className="vl-section-lead">
                When a photographer shares a shoot with a model — or a
                creator shares with a partner — both sides get a real home
                for the work. Each keeps their own ratings, favorites, and
                notes. Neither can erase the other's view.
              </p>
              <ul className="vl-features">
                <li>
                  <span className="vl-feat-icon">⌘</span>
                  <div>
                    <strong>Independent metadata</strong>
                    <span>Your ratings stay yours. Your collaborator's stay theirs. The frames are shared, the perspective is not.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⌷</span>
                  <div>
                    <strong>Quiet conversations on every frame</strong>
                    <span>Comment on a single image. Tag a moment to revisit. No group chat noise — just the work.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⊞</span>
                  <div>
                    <strong>Permanent for the model</strong>
                    <span>If a photographer leaves Vuelette, the model keeps her copy of every shoot they made together.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pullquote */}
      <section className="vl-section vl-section-tinted">
        <div className="vl-container">
          <p className="vl-pullquote">
            "A portfolio shouldn't be a folder you mail. It should be a{' '}
            <em>place you send people to</em> — and a place that stays as good
            as the work."
            <span className="vl-attr">Vuelette · Why we built this</span>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="vl-cta-block">
        <div className="vl-container">
          <div className="vl-section-eyebrow">Closed beta</div>
          <h2 className="vl-section-title" style={{ marginInline: 'auto' }}>
            Currently in invitation-only beta.
          </h2>
          <p>
            We're working closely with a small group of image-makers in
            Barcelona, Stockholm and beyond to get every detail right. Request access
            and we'll let you in as we open the next round.
          </p>
          <a href={REQUEST_ACCESS_MAILTO} className="vl-btn vl-btn-primary">
            Request access →
          </a>
        </div>
      </section>
    </LandingShell>
  );
}
